import { Box, Divider, Stack, Typography } from '@mui/material';
import StandardModal from '@/components/common/standard.modal';
import ReactMarkdown from 'react-markdown';
import { format, parseISO } from 'date-fns';
import type { AcceptedTermDto } from '@/types/dtos/contributors/response/accepted-term.dto';
import { TERM_TYPE } from '@/types/dtos/events/term.dto';
import { useTranslations } from '@/i18n/use-translations';

interface AcceptedTermsModalProps {
  open: boolean;
  onClose: () => void;
  acceptedTerms?: AcceptedTermDto[];
}

const AcceptedTermsModal = ({ open, onClose, acceptedTerms = [] }: AcceptedTermsModalProps) => {
  const t = useTranslations();

  const sortedTerms = [...acceptedTerms].sort((a, b) => {
    const aTime = a.acceptedAt ? new Date(a.acceptedAt).getTime() : 0;
    const bTime = b.acceptedAt ? new Date(b.acceptedAt).getTime() : 0;
    return bTime - aTime;
  });

  const termTypeLabel = (type: AcceptedTermDto['type']) => {
    if (type === TERM_TYPE.CONTRIBUTION_TERMS) {
      return t('myEventsList.contributionsList.termsModalTitle');
    }
    return type;
  };

  const formatAcceptedAt = (acceptedAt: string) => {
    try {
      return format(parseISO(acceptedAt), 'dd.MM.yyyy HH:mm');
    } catch {
      return acceptedAt;
    }
  };

  return (
    <StandardModal
      open={open}
      close={onClose}
      title={t('user.contributionsList.acceptedTermsTitle')}
      menu={false}
      size="lg"
      height="600px"
      maxHeight="calc(100vh - 64px)"
      width="800px"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
        {sortedTerms.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Typography variant="body1" color="text.secondary">
              {t('user.contributionsList.noAcceptedTerms')}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2} sx={{ flex: 1, overflow: 'auto', pr: 0.5 }}>
            {sortedTerms.map((term) => (
              <Box
                key={`${term.id}-${term.version}-${term.acceptedAt}`}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'background.default',
                }}
              >
                <Stack spacing={0.5} sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle2">{termTypeLabel(term.type)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('user.contributionsList.acceptedTermsVersion', {
                      version: String(term.version),
                    } as Record<string, string>)}
                    {' · '}
                    {t('user.contributionsList.acceptedTermsAcceptedAt', {
                      date: formatAcceptedAt(term.acceptedAt),
                    } as Record<string, string>)}
                  </Typography>
                </Stack>
                <Divider />
                <Box
                  sx={{
                    'p': 2,
                    '& > *': {
                      'marginTop': 0,
                      'marginBottom': '1em',
                      '&:last-child': {
                        marginBottom: 0,
                      },
                    },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      marginTop: '1em',
                      marginBottom: '0.5em',
                      fontWeight: 600,
                    },
                    '& ul, & ol': {
                      paddingLeft: '1.5em',
                      marginBottom: '1em',
                    },
                    '& li': {
                      marginBottom: '0.5em',
                    },
                    '& p': {
                      marginBottom: '1em',
                    },
                    '& strong': {
                      fontWeight: 600,
                    },
                    '& em': {
                      fontStyle: 'italic',
                    },
                  }}
                >
                  <ReactMarkdown>{term.text}</ReactMarkdown>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </StandardModal>
  );
};

export default AcceptedTermsModal;
